import Phaser from "phaser";
import { PlayerSprite } from "./PlayerSprite";
import type { ScreenPos } from "../map/Iso";
import type { PlayerPos } from "@/net/types";

export class Avatars {
  private readonly sprites = new Map<string, PlayerSprite>();

  constructor(
    private readonly scene: Phaser.Scene,
    private selfId: string,
    private readonly origin: ScreenPos,
  ) {}

  setSelfId(id: string): void {
    this.selfId = id;
  }

  upsert(userId: string, pos: PlayerPos, displayName?: string): PlayerSprite {
    const existing = this.sprites.get(userId);
    if (existing) {
      existing.setTarget(pos);
      if (displayName) existing.setLabel(displayName);
      return existing;
    }
    const sprite = new PlayerSprite(this.scene, userId, pos, userId === this.selfId, this.origin, displayName);
    this.sprites.set(userId, sprite);
    return sprite;
  }

  remove(userId: string): void {
    const s = this.sprites.get(userId);
    if (!s) return;
    s.destroy();
    this.sprites.delete(userId);
  }

  get(userId: string): PlayerSprite | undefined {
    return this.sprites.get(userId);
  }

  self(): PlayerSprite | undefined {
    return this.sprites.get(this.selfId);
  }

  tick(dtMs: number): void {
    for (const s of this.sprites.values()) s.tick(dtMs);
  }

  userIds(): IterableIterator<string> {
    return this.sprites.keys();
  }

  /** Returns a snapshot of all tracked tile positions keyed by userId. */
  getPositions(): Map<string, { x: number; y: number }> {
    const out = new Map<string, { x: number; y: number }>();
    for (const [id, sprite] of this.sprites) {
      out.set(id, sprite.getTilePos());
    }
    return out;
  }

  clear(): void {
    for (const s of this.sprites.values()) s.destroy();
    this.sprites.clear();
  }
}
